import { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'reprev-install-prompt';
const MAX_DISMISSALS = 3;

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;

    if (isStandalone) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    const data = stored ? JSON.parse(stored) : { dismissCount: 0, installed: false };

    if (data.installed || data.dismissCount >= MAX_DISMISSALS) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      setShowIOSPrompt(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, installed: true }));
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const data = stored ? JSON.parse(stored) : { dismissCount: 0 };
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...data,
      dismissCount: (data.dismissCount || 0) + 1
    }));
    setDismissed(true);
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
  };

  if (dismissed || (!deferredPrompt && !showIOSPrompt)) return null;

  return (
    <div className="fixed left-4 right-4 z-[45] block md:hidden animate-in slide-in-from-bottom-4 duration-300" style={{ bottom: 'calc(7rem + env(safe-area-inset-bottom, 0px))' }}>
      <div className="bg-card border border-border rounded-xl p-4 shadow-lg flex items-center gap-3">
        <div className="flex-shrink-0">
          {deferredPrompt ? (
            <Download className="h-6 w-6 text-primary" />
          ) : (
            <Share className="h-6 w-6 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">Install RepRev.ai</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {deferredPrompt
              ? "Add to your home screen for the best experience"
              : "Tap Share, then \"Add to Home Screen\""}
          </p>
        </div>
        {deferredPrompt && (
          <Button size="sm" onClick={handleInstall} className="flex-shrink-0">
            Install
          </Button>
        )}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
